import axios from "axios";
import React, { useEffect, useState } from "react";
import TempoButton, { ButtonSize, ButtonType } from "../Core/TempoButton";
import { useDispatch } from "react-redux";
import { setIFrameChanged } from "@/store/iframeSlice";
import { toast } from "sonner";
import {
  getAxiosErorrMessage,
  getUnexpiredToken,
  useActiveProject,
} from "@/utils/utils";
import { track } from "@/utils/events/tracker";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useCanvasId } from "@/utils/utils";
import { useSession } from "@clerk/clerk-react";
import SimpleMonacoEditor from "./SimpleMonacoEditor";
import useActivityStore from "@/store/zustand/activityStore";
interface Props {
  filename: string;

  substringStart?: number;
  substringEnd?: number;

  trimWhitespace?: boolean;
  forceLanguage?: string;
}

const FileEditor: React.FC<Props> = (props) => {
  const canvasId = useCanvasId();

  const { session } = useSession();

  const dispatch = useDispatch();

  const updateLookupsBasedOnPartialResponse = useCodebaseStore(
    (state) => state.updateLookupsBasedOnPartialResponse
  );

  const [fullFileContents, setFullFileContents] = useState<any>(null);
  const [substringContents, setSubstringContents] = useState<any>(null);
  const [editorValue, setEditorValue] = useState<any>(null);

  const [internalSubstringStart, setInternalSubstringStart] =
    useState<any>(null);
  const [internalSubstringEnd, setInternalSubstringEnd] = useState<any>(null);

  const [savingFile, setSavingFile] = useState(false);

  const triggerRefetchActivityStream = useActivityStore(
    (state) => state.triggerRefetchActivityStream
  );

  const loadFile = async () => {
    if (!canvasId) {
      return;
    }

    const supabaseJWT = await getUnexpiredToken(session);

    try {
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/canvases/${canvasId}/file`,
        {
          params: {
            filename: props.filename,
          },
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        }
      );

      let substringContents = response.data.fileContents.substring(
        props.substringStart || 0,
        props.substringEnd || response.data.fileContents.length
      );

      if (props.trimWhitespace) {
        let newSubstringStart = substringContents.search(/\S|$/);
        setInternalSubstringStart(
          (props.substringStart || 0) + newSubstringStart
        );
        substringContents = substringContents.substring(
          newSubstringStart,
          substringContents.length
        );

        const trimmedSubstringContents = substringContents.trim();
        const trimmedOffEnd =
          substringContents.length - trimmedSubstringContents.length;
        setInternalSubstringEnd(
          (props.substringEnd || response.data.fileContents.length) -
            trimmedOffEnd
        );
        substringContents = trimmedSubstringContents;
      } else {
        setInternalSubstringStart(props.substringStart || 0);
        setInternalSubstringEnd(
          props.substringEnd || response.data.fileContents.length
        );
      }

      setSubstringContents(substringContents);
      setEditorValue(substringContents);
      setFullFileContents(response.data.fileContents);
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  const saveFileChanges = async () => {
    const newContents =
      fullFileContents.substring(0, internalSubstringStart) +
      editorValue +
      fullFileContents.substring(internalSubstringEnd, fullFileContents.length);

    track("FILE_SAVE", {
      from: "FileEditor",
    });

    const supabaseJWT = await getUnexpiredToken(session);

    axios
      .post(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/canvases/${canvasId}/file`,
        {
          filename: props.filename,
          fileContents: newContents,
        },
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        }
      )
      .then((res: any) => {
        if (res?.data) {
          updateLookupsBasedOnPartialResponse(res.data);
          triggerRefetchActivityStream();
        }

        // Clear unsaved changes
        loadFile();

        // Notify the iframe changed
        dispatch(setIFrameChanged());

        setSavingFile(false);
      })
      .catch((err: any) => {
        console.error(err);
        toast.error("Could not save file: " + getAxiosErorrMessage(err));

        setSavingFile(false);
      });
  };

  useEffect(() => {
    if (props.filename && canvasId) {
      loadFile();
    } else {
      setSubstringContents(null);
      setEditorValue(null);
      setInternalSubstringStart(null);
      setInternalSubstringEnd(null);
      setFullFileContents(null);
    }
  }, [props.filename, canvasId, props.substringStart, props.substringEnd]);

  const getEditor = () => {
    let language = props.forceLanguage || "text";
    if (props.filename && !props.forceLanguage) {
      if (props.filename.endsWith("css")) {
        language = "css";
      } else if (
        props.filename.endsWith("js") ||
        props.filename.endsWith("jsx") ||
        props.filename.endsWith("mjs") ||
        props.filename.endsWith("cjs")
      ) {
        language = "javascript";
      } else if (
        props.filename.endsWith("tsx") ||
        props.filename.endsWith("ts") ||
        props.filename.endsWith("cts") ||
        props.filename.endsWith("mts")
      ) {
        language = "javascript";
      } else if (props.filename.endsWith("json")) {
        language = "json";
      } else if (props.filename.endsWith("md")) {
        language = "markdown";
      } else if (props.filename.endsWith("html")) {
        language = "html";
      } else if (props.filename.endsWith("xml")) {
        language = "xml";
      } else if (
        props.filename.endsWith("yaml") ||
        props.filename.endsWith("yml")
      ) {
        language = "yaml";
      } else if (props.filename.endsWith("sh")) {
        language = "shell";
      } else {
        language = "text";
      }
    }

    return (
      <SimpleMonacoEditor
        language={language}
        key={language} // Refresh when language changes
        value={editorValue || ""}
        className={"w-[95%]"}
        onChange={(newValue: string) => {
          if (savingFile) {
            return;
          }

          setEditorValue(newValue);
        }}
      />
    );
  };

  return (
    <div className="h-full w-full flex flex-col gap-2">
      {getEditor()}
      {substringContents && substringContents !== editorValue && (
        <TempoButton
          text={"Save"}
          type={ButtonType.Action}
          size={ButtonSize.Small}
          className="save-file-editor-changes-button"
          onClick={() => {
            setSavingFile(true);
            saveFileChanges();
          }}
          loading={savingFile}
        />
      )}
    </div>
  );
};

export default React.memo(FileEditor);
